import { Mail, Phone, Globe } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { ShareArticleSection } from "../ShareArticleSection";

const DigitalConcierge = () => {
  return (
    <div className="modal-content-body">
      <ShareArticleSection />
      <div className="modal-section">
        <div className="flex-content">
          <div>
            <h3>Use Case</h3>
          </div>
          <div>
            <p>
              Deploy a branded, GenAI-powered digital concierge to automate
              customer support and user journeys - across web, app, and contact
              centre touchpoints - streamlining service and boosting engagement
            </p>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Solution</h3>
          </div>
          <div>
            <a
              href="https://aws.amazon.com/marketplace/pp/prodview-fe3jofu5kegzq?sr=0-5&ref_=beagle&applicationId=AWSMPContessa"
              target="_blank"
              rel="noopener noreferrer"
            >
              Digital AI Concierge
            </a>{" "}
            (AWS Bedrock, Amazon Lex, custom GenAI, workflow automation)
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Target Segment</h3>
          </div>
          <div>
            <ul>
              <li>
                SMB/Enterprise companies with high-volume online, mobile, or app
                engagement
              </li>
              <li>
                Contact centre teams seeking to shift from human to digital
                first
              </li>
              <li>
                Brands prioritising automation, scalable self-service, and AI
                innovation
              </li>
            </ul>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Target Personas</h3>
          </div>
          <div>
            <p>
              Head of Digital, Chief Customer Officer, CX Lead, Product Owner,
              Head of Automation
            </p>
          </div>
        </div>
      </div>

      {/* Opportunity & Qualification */}
      <div className="modal-section">
        <h2 className="modal-section-title">Opportunity and Qualification</h2>
        <div className="modal-section-grid">
          <div className="modal-column">
            <h3 className="modal-subtitle">
              What is the customer trying to accomplish?
            </h3>
            <ul>
              <li>Automate repetitive support and FAQs</li>
              <li>
                Provide 24/7 digital assistance with contextual,
                brand-consistent AI
              </li>
              <li>Lower contact centre workload and cost</li>
              <li>
                Enable seamless self-service across multiple digital channels
              </li>
              <li>
                Unlock advanced analytics and insight from customer journeys
              </li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Key Words/ Questions</h3>
            <ul>
              <li>Lacking new feature adoption or stuck on legacy workflows</li>
              <li>Want to improve/support AI, reporting, or automation</li>
              <li>
                Unsure if the platform is optimised for cost, compliance, or CX
              </li>
              <li>Seeking managed or advisory support for contact centre</li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Pain Point/ Challenge</h3>
            <ul>
              <li>
                Want 24/7 customer help or chat without increasing headcount
              </li>
              <li>Need a chatbot or AI persona that “fits our brand”</li>
              <li>Current chat automation is too basic or disconnected</li>
              <li>Struggling to answer repetitive questions quickly</li>
              <li>Interested in trying GenAI or AWS Bedrock for CX</li>
              <li>Want to connect AI to CRM/workflows</li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Qualifying Questions</h3>
            <ul>
              <li>
                Are you fully leveraging the latest AWS Connect features and AI
                capabilities?
              </li>
              <li>
                Would you value regular health checks, innovation reviews, and
                cost optimisation?
              </li>
              <li>Has performance plateaued since your initial launch?</li>
              <li>
                Would ongoing advisory and managed support free your team for
                higher-value work?
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Proposed Solution */}
      <div className="modal-section">
        <h2 className="modal-section-title">Proposed Solution / Value</h2>
        <div className="flex-content-half">
          <div>
            <h3>Solution Description</h3>
            <ul style={{ marginTop: "10px" }}>
              <li>
                Bespoke AI persona design - trained on your brand voice,
                knowledge, and workflows
              </li>
              <li>
                End-to-end deployment - including discovery, process
                documentation, integrations, happy-path design, and safety
                guardrails
              </li>
              <li>
                Live monitoring & analytics - with continuous performance tuning
              </li>
              <li>
                Seamless escalation - smooth handoff to human agents when needed
              </li>
            </ul>
          </div>
          <div>
            <h3>Solution Benefits to AWS Customers</h3>
            <ul style={{ marginTop: "10px" }}>
              <li>30% reduction in contact centre workload</li>
              <li>
                Always-on digital engagement with consistent brand experience
              </li>
              <li>
                Actionable journey analytics driving continuous improvement
              </li>
              <li>Lower TCO while enhancing overall service quality</li>
            </ul>
          </div>
        </div>

        <h2 className="modal-section-title" style={{ marginTop: "30px" }}>
          Objection Handling
        </h2>
        <div className="flex-content-half">
          <div>
            <p>
              <b>Bots are too impersonal:</b> Customised persona matches your
              brand voice, so customers feel heard.
            </p>

            <p>
              <b>AI can't handle our unique processes:</b> Fully trained on your
              workflows, with seamless human escalation when needed.
            </p>
            <p>
              <b>Security & data risks: </b>Built on robust, AWS-native,
              privacy-first automation with full guardrails.
            </p>
            <p>
              <b>Too complex:</b> iCXeed manages strategy, integrations,
              training, and support end-to-end.
            </p>
            <p>
              <b>Our volumes may be too low:</b> Scalable from pilot to
              enterprise with fast ROI.
            </p>
          </div>
        </div>
      </div>

      <div className="modal-footnote" style={{ border: 0 }}>
        <h3>Primary Partner Contact:</h3>
        <div className="flex-box-blk">
          <div>
            <img
              src="https://www.icxeed.ai/wp-content/themes/icxeed-website-2025/portal-assets/images/thumbnails/ryan-thumbnail.png"
              alt="ryan rayner"
            />
          </div>
          <div>
            <h2>Ryan Rayner</h2>
            <p>Co-Founder & Chief Customer Officer</p>
          </div>
        </div>
        <div className="flex-box-blk footnote-flex-box">
          <div className="flex-box-blk">
            <div>
              <Mail size={20} color="#01C2F3" />
            </div>
            <div>
              <a href="mailto:ryan.rayner@icxeed.ai">ryan.rayner@icxeed.ai</a>
            </div>
          </div>
          <div className="flex-box-blk">
            <div>
              <Phone size={20} color="#01C2F3" />
            </div>
            <div>
              <a href="tel:+61438286198">+61 438 286 198</a>
            </div>
          </div>
        </div>

        <div className="flex-box-blk footnote-flex-box">
          <div className="flex-box-blk">
            <div>
              <FontAwesomeIcon icon={faLinkedinIn} size="lg" color="#01C2F3" />
            </div>
            <div>
              <a
                href=" https://www.linkedin.com/in/ryanrayner/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.linkedin.com/in/ryanrayner/
              </a>
            </div>
          </div>
          <div className="flex-box-blk">
            <div>
              <Globe size={20} color="#01C2F3" />
            </div>
            <div>
              <a
                href="https://icxeed.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://icxeed.ai/
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalConcierge;
